import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, forkJoin } from 'rxjs';
import { switchMap, map, catchError, shareReplay } from 'rxjs/operators';
import { CIMediaModel } from 'src/app/models/CIMedia.model';

import { CINewsModel } from 'src/app/models/CINews.model';
import { environment } from 'src/environments/environment';
import { UserService } from '../user/user.service';
import { CIStructuredNewsModel } from 'src/app/models/CIStructuredNews.model';
import { RedirectProperty } from 'src/app/types';

@Injectable({
  providedIn: 'root',
})
export class CIService {
  constructor(
    public http: HttpClient,
    private userService: UserService,
  ) {
    this.apiUrl = environment.ciApiUrl;

    // armazenamos apenas os caminhos relativos; buildUrl() monta a URL completa
    this.authUrl = 'api/auth/sign-in';
    this.newsUrl = 'api/news/user';
    this.mediaUrl = 'api/file-media/user?fileType=VIDEO';
    this.signedUrl = 'api/s3/presigned-urls';
  }

  private apiUrl: string;
  private authUrl: string;
  private newsUrl: string;
  private mediaUrl: string;
  private signedUrl: string;
  private authToken$: Observable<string> | null = null;
  private redirectToken: string | null = null;
  private caminhosIntencionais: RedirectProperty = environment.redirect.caminhosIntencionais;

  headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');

  /**
   * Monta a URL completa para um endpoint. Se o endpoint já for absoluto, retorna-o.
   */
  private buildUrl(endpoint: string): string {
    if (!endpoint) {
      return '';
    }

    if (/^https?:\/\//i.test(endpoint)) {
      return endpoint;
    }

    const base = (this.apiUrl || '').replace(/\/$/, '');
    const e = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    return base + e;
  }

  /**
   * Retorna um Observable cacheado que emite o token de autenticação (accessToken)
   */
  private getAuthToken$(): Observable<string> {
    if (this.authToken$) {
      return this.authToken$;
    }

    this.authToken$ = this.userService.encryptToken().pipe(
      switchMap((enc: any) => {
        this.redirectToken = enc;

        const body: any = { token: enc };

        return this.http
          .post<{ accessToken: string }>(this.buildUrl(this.authUrl), body)
          .pipe(map(r => (r && r.accessToken ? r.accessToken : '')));
      }),
      catchError(() => of('')),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    return this.authToken$;
  }

  /**
   * Força a renovação do token
   */
  refreshAuthToken(): void {
    this.authToken$ = null;
  }

  /**
   * Retorna notícias estruturadas: { id, title, link, createdAt, imageUrl }
   */
  getStructuredNews(amount = 2, lang = 'pt'): Observable<Array<CIStructuredNewsModel>> {
    return this.getAuthToken$().pipe(
      switchMap(token => {
        const h = token ? this.headers.set('Authorization', `Bearer ${token}`) : this.headers;

        const news$ = this.http
          .get<CINewsModel[]>(this.buildUrl(this.newsUrl), { headers: h })
          .pipe(catchError(() => of([])));

        const media$ = this.http
          .get<CIMediaModel[]>(this.buildUrl(this.mediaUrl), { headers: h })
          .pipe(catchError(() => of([])));

        // Mantemos headers junto para uso na chamada de assinatura
        return forkJoin([of(h), news$, media$]);
      }),
      switchMap(([h, newsList, mediaList]: [HttpHeaders, CINewsModel[], CIMediaModel[]]) => {
        const mappedNews = (newsList || [])
          .map((item: CINewsModel) => {
            const translations = item.translations || [];
            const translation = translations.find((t: any) => t.lang === lang) || translations[0] || {};
            const date = item.createdAt || item.publishDate || item.updatedAt;
            const rawThumb = translation.thumbnail || '';

            // monta o link redirect da notícia no CI
            const redirectPath = `${this.caminhosIntencionais.link}?token=${encodeURIComponent(this.redirectToken || '')}&redirect=noticias/${item._id}/pt/news`;

            return {
              id: item._id || '',
              title: translation.title || '',
              redirectPath,
              createdAt: date ? new Date(date).toISOString() : undefined,
              imageUrl: rawThumb || undefined,
              mediaType: 'image',
            } as CIStructuredNewsModel;
          })
          .filter(n => !n.imageUrl?.includes('.pdf'));

        const mappedMedia = (mediaList || [])
          .map((item: CIMediaModel) => {
            const translations = item.translations || [];
            const translation = translations.find((t: any) => t.lang === lang) || translations[0] || {};
            const date = item.createdAt || item.updatedAt;
            const rawKey = item.fileKey || '';

            // monta o link redirect da media no CI
            const redirectPath = `${this.caminhosIntencionais.link}?token=${encodeURIComponent(this.redirectToken || '')}&redirect=midias/${item._id}/pt`;

            return {
              id: item._id || '',
              title: translation.title || item.originalName || '',
              redirectPath,
              createdAt: date ? new Date(date).toISOString() : undefined,
              imageUrl: rawKey || undefined,
              mediaType: item.fileType === 'VIDEO' ? 'video' : 'image',
            } as CIStructuredNewsModel;
          })
          .filter(n => !n.imageUrl?.includes('.pdf'));

        const merged = [...mappedNews, ...mappedMedia];

        merged.sort((a, b) => {
          const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return db - da;
        });

        const limited = merged.slice(0, amount);
        const paths = limited.map(m => m.imageUrl).filter(Boolean);

        if (!paths.length) {
          return of(
            limited.map(m => ({
              id: m.id,
              title: m.title,
              redirectPath: '',
              createdAt: m.createdAt,
              imageUrl: m.imageUrl,
            })),
          );
        }

        // Gera links assinados via rota signedUrl
        return this.http
          .post<any>(this.buildUrl(this.signedUrl), { fileNames: paths, operation: 'GET' }, { headers: h })
          .pipe(
            catchError(() => of(null)),
            map(signRes => {
              const getSignedForPath = (path: string): string => {
                if (!signRes) {
                  return '';
                }

                const found = (signRes || []).find((s: any) => s.includes(path));

                return found || '';
              };

              return limited.map(m => ({
                id: m.id,
                title: m.title,
                redirectPath: m.redirectPath,
                createdAt: m.createdAt,
                imageUrl: m.imageUrl ? getSignedForPath(m.imageUrl) : '',
                mediaType: m.mediaType || 'image',
              }));
            }),
          );
      }),
    );
  }
}
